import { useAppKit, useAppKitAccount, useDisconnect } from '@reown/appkit/react'
import { useCallback, useEffect, useState } from 'react'
import { env } from '@/config/env.ts'

interface MockWalletState {
  address: string
  connected: boolean
}

export function useWallet() {
  const { open } = useAppKit()
  const { address, isConnected, status } = useAppKitAccount()
  const { disconnect } = useDisconnect()
  const [mockWallet, setMockWallet] = useState<MockWalletState | null>(null)

  useEffect(() => {
    if (!env.enableDebug || typeof window === 'undefined') return

    const loadMock = () => {
      try {
        const raw = window.localStorage.getItem('bitslegacy_mock_wallet')
        if (!raw) {
          setMockWallet(null)
          return
        }
        const parsed = JSON.parse(raw) as MockWalletState
        if (parsed?.connected && typeof parsed.address === 'string') {
          setMockWallet(parsed)
          return
        }
      } catch {
        // ignore malformed localStorage payload
      }
      setMockWallet(null)
    }

    loadMock()
    window.addEventListener('wallet-mock-connected', loadMock)
    window.addEventListener('storage', loadMock)
    return () => {
      window.removeEventListener('wallet-mock-connected', loadMock)
      window.removeEventListener('storage', loadMock)
    }
  }, [])

  const effectiveAddress = mockWallet?.connected ? mockWallet.address : address
  const effectiveConnected = mockWallet?.connected ? true : isConnected

  const connect = useCallback(() => {
    if (env.enableDebug && mockWallet?.connected) return
    open()
  }, [open, mockWallet])

  const disconnectWallet = useCallback(async () => {
    if (
      env.enableDebug &&
      typeof window !== 'undefined' &&
      mockWallet?.connected
    ) {
      window.localStorage.removeItem('bitslegacy_mock_wallet')
      setMockWallet(null)
      return
    }
    await disconnect()
  }, [disconnect, mockWallet])

  const shortenedAddress = effectiveAddress
    ? `${effectiveAddress.slice(0, 6)}...${effectiveAddress.slice(-4)}`
    : null

  return {
    address: effectiveAddress,
    shortenedAddress,
    isConnected: effectiveConnected,
    isConnecting: status === 'connecting' || status === 'reconnecting',
    connect,
    disconnect: disconnectWallet,
  }
}
